import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { api } from '../../services/api';

const HistoryModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  z-index: 1300;
  display: ${props => props.$show ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  padding: 1rem;
`;

const HistoryContainer = styled.div`
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 1200px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
`;

const HistoryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 2px solid #eee;
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  color: white;
`;

const HistoryTitle = styled.h2`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 1.8rem;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  opacity: 0.8;
  
  &:hover {
    opacity: 1;
    background: rgba(255, 255, 255, 0.1);
  }
`;

const HistoryContent = styled.div`
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const FilterSection = styled.div`
  padding: 1rem 1.5rem;
  background: #f8f9fa;
  border-bottom: 1px solid #eee;
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  
  label {
    font-size: 0.8rem;
    color: #666;
    font-weight: 500;
  }
  
  input, select {
    padding: 0.5rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 0.9rem;
    
    &:focus {
      outline: none;
      border-color: #28a745;
    }
  }
`;

const FilterButton = styled.button`
  align-self: end;
  padding: 0.5rem 1rem;
  background: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  
  &:hover {
    background: #218838;
  }
`;

const SummarySection = styled.div`
  padding: 1rem 1.5rem;
  background: #e8f5e8;
  border-bottom: 1px solid #ddd;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
`;

const SummaryCard = styled.div`
  text-align: center;
  
  .value {
    font-size: 1.5rem;
    font-weight: 700;
    color: #28a745;
    margin-bottom: 0.25rem;
  }
  
  .label {
    font-size: 0.8rem;
    color: #666;
    text-transform: uppercase;
    font-weight: 500;
  }
`;

const TransactionList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
`;

const TransactionCard = styled.div`
  background: white;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 0.75rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: all 0.2s;
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-1px);
  }
`;

const TransactionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
`;

const TransactionNumber = styled.div`
  font-weight: 600;
  font-size: 1.1rem;
  color: #333;
`;

const TransactionStatus = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  
  ${props => {
    switch (props.$status) {
      case 'completed':
        return 'background: #d4edda; color: #155724;';
      case 'pending':
        return 'background: #fff3cd; color: #856404;';
      case 'failed':
        return 'background: #f8d7da; color: #721c24;';
      case 'cancelled':
        return 'background: #e2e3e5; color: #495057;';
      default:
        return 'background: #e9ecef; color: #6c757d;';
    }
  }}
`;

const TransactionDetails = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 1rem;
  margin-bottom: 0.75rem;
`;

const DetailItem = styled.div`
  .label {
    font-size: 0.8rem;
    color: #666;
    margin-bottom: 0.25rem;
  }
  
  .value {
    font-weight: 500;
    color: #333;
  }
`;

const TransactionAmount = styled.div`
  text-align: right;
  padding-top: 0.5rem;
  border-top: 1px solid #eee;
  
  .amount {
    font-size: 1.25rem;
    font-weight: 700;
    color: ${props => props.$type === 'refund' ? '#dc3545' : '#28a745'};
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  
  .spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #28a745;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  color: #666;
  text-align: center;
  
  .icon {
    font-size: 4rem;
    margin-bottom: 1rem;
    opacity: 0.5;
  }
  
  .message {
    font-size: 1.1rem;
    margin-bottom: 0.5rem;
  }
  
  .submessage {
    font-size: 0.9rem;
    opacity: 0.7;
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
  border-top: 1px solid #eee;
  
  button {
    padding: 0.5rem 0.75rem;
    border: 1px solid #ddd;
    background: white;
    cursor: pointer;
    border-radius: 4px;
    
    &:hover:not(:disabled) {
      background: #f8f9fa;
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    
    &.active {
      background: #28a745;
      color: white;
      border-color: #28a745;
    }
  }
  
  .page-info {
    margin: 0 1rem;
    font-size: 0.9rem;
    color: #666;
  }
`;

const TicketHistory = ({ isOpen, onClose, clientId }) => {
  const [filters, setFilters] = useState({
    status: '',
    paymentMethod: '',
    dateFrom: '',
    dateTo: '',
    page: 1
  });

  // Get transactions
  const { data: transactionsData, isLoading: transactionsLoading, refetch: refetchTransactions } = useQuery({
    queryKey: ['tpv-transactions', filters, clientId],
    queryFn: () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      // Add clientId filter if provided
      if (clientId) {
        params.append('clientId', clientId);
      }
      return api.get(`/tpv/transactions?${params.toString()}`);
    },
    enabled: isOpen,
    staleTime: 30000 // 30 seconds
  });

  // Get summary
  const { data: summaryData, isLoading: summaryLoading } = useQuery({
    queryKey: ['tpv-transactions-summary', { dateFrom: filters.dateFrom, dateTo: filters.dateTo, clientId }],
    queryFn: () => {
      const params = new URLSearchParams();
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.append('dateTo', filters.dateTo);
      // Add clientId filter if provided
      if (clientId) params.append('clientId', clientId);
      return api.get(`/tpv/transactions/summary?${params.toString()}`);
    },
    enabled: isOpen,
    staleTime: 60000 // 1 minute
  });

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const applyFilters = () => {
    refetchTransactions();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPaymentMethodLabel = (method) => {
    const labels = {
      cash: '💵 Efectivo',
      card: '💳 Tarjeta',
      transfer: '🏦 Transfer.',
      mixed: '🔄 Mixto'
    };
    return labels[method] || method;
  };

  const transactions = transactionsData?.data?.data?.transactions || [];
  const transactionsMeta = transactionsData?.data?.data || {};
  const summary = summaryData?.data?.data || {};

  return (
    <HistoryModal $show={isOpen}>
      <HistoryContainer>
        <HistoryHeader>
          <HistoryTitle>📊 Historial de Tickets TPV</HistoryTitle>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </HistoryHeader>

        <HistoryContent>
          {/* Filters */}
          <FilterSection>
            <FilterGroup>
              <label>Estado</label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">Todos</option>
                <option value="completed">Completado</option>
                <option value="pending">Pendiente</option>
                <option value="failed">Fallido</option>
                <option value="cancelled">Cancelado</option>
              </select>
            </FilterGroup>

            <FilterGroup>
              <label>Método Pago</label>
              <select
                value={filters.paymentMethod}
                onChange={(e) => handleFilterChange('paymentMethod', e.target.value)}
              >
                <option value="">Todos</option>
                <option value="cash">Efectivo</option>
                <option value="card">Tarjeta</option>
                <option value="transfer">Transferencia</option>
                <option value="mixed">Mixto</option>
              </select>
            </FilterGroup>

            <FilterGroup>
              <label>Desde</label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              />
            </FilterGroup>

            <FilterGroup>
              <label>Hasta</label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              />
            </FilterGroup>

            <FilterButton onClick={applyFilters}>
              🔍 Filtrar
            </FilterButton>
          </FilterSection>

          {/* Summary */}
          {!summaryLoading && summary.totalTransactions > 0 && (
            <SummarySection>
              <SummaryCard>
                <div className="value">{summary.totalTransactions || 0}</div>
                <div className="label">Total Tickets</div>
              </SummaryCard>
              <SummaryCard>
                <div className="value">{summary.salesCount || 0}</div>
                <div className="label">Ventas</div>
              </SummaryCard>
              <SummaryCard>
                <div className="value">{summary.refundsCount || 0}</div>
                <div className="label">Devoluciones</div>
              </SummaryCard>
              <SummaryCard>
                <div className="value">{formatCurrency(summary.totalSales)}</div>
                <div className="label">Total Ventas</div>
              </SummaryCard>
              <SummaryCard>
                <div className="value">{formatCurrency(summary.netAmount)}</div>
                <div className="label">Importe Neto</div>
              </SummaryCard>
              <SummaryCard>
                <div className="value">{formatCurrency(summary.averageTransactionValue)}</div>
                <div className="label">Ticket Medio</div>
              </SummaryCard>
            </SummarySection>
          )}

          {/* Transactions List */}
          <TransactionList>
            {transactionsLoading ? (
              <LoadingSpinner>
                <div className="spinner"></div>
              </LoadingSpinner>
            ) : transactions.length === 0 ? (
              <EmptyState>
                <div className="icon">🧾</div>
                <div className="message">No hay tickets para mostrar</div>
                <div className="submessage">Ajusta los filtros o verifica que hay transacciones procesadas</div>
              </EmptyState>
            ) : (
              <>
                {transactions.map(transaction => (
                  <TransactionCard key={transaction.id}>
                    <TransactionHeader>
                      <TransactionNumber>
                        Ticket #{transaction.transactionNumber}
                      </TransactionNumber>
                      <TransactionStatus $status={transaction.status}>
                        {transaction.status}
                      </TransactionStatus>
                    </TransactionHeader>

                    <TransactionDetails>
                      <DetailItem>
                        <div className="label">Fecha y Hora</div>
                        <div className="value">{formatDate(transaction.processedAt)}</div>
                      </DetailItem>
                      
                      <DetailItem>
                        <div className="label">Método de Pago</div>
                        <div className="value">{getPaymentMethodLabel(transaction.paymentMethod)}</div>
                      </DetailItem>
                      
                      <DetailItem>
                        <div className="label">Sesión TPV</div>
                        <div className="value">#{transaction.session?.sessionNumber || 'N/A'}</div>
                      </DetailItem>

                      {transaction.order && (
                        <>
                          <DetailItem>
                            <div className="label">Pedido</div>
                            <div className="value">#{transaction.order.orderNumber}</div>
                          </DetailItem>
                          
                          <DetailItem>
                            <div className="label">Descripción</div>
                            <div className="value">{transaction.order.displayName || 'Sin descripción'}</div>
                          </DetailItem>
                          
                          <DetailItem>
                            <div className="label">Cliente</div>
                            <div className="value">{transaction.order.clientReference || 'N/A'}</div>
                          </DetailItem>
                        </>
                      )}

                      <DetailItem>
                        <div className="label">Procesado por</div>
                        <div className="value">
                          {transaction.processedBy?.firstName} {transaction.processedBy?.lastName}
                        </div>
                      </DetailItem>
                    </TransactionDetails>

                    <TransactionAmount $type={transaction.type}>
                      <div className="amount">
                        {transaction.type === 'refund' && '-'}
                        {formatCurrency(Math.abs(transaction.finalAmount))}
                      </div>
                    </TransactionAmount>
                  </TransactionCard>
                ))}

                {/* Pagination */}
                {transactionsMeta.totalPages > 1 && (
                  <Pagination>
                    <button 
                      onClick={() => handlePageChange(filters.page - 1)}
                      disabled={filters.page <= 1}
                    >
                      « Anterior
                    </button>
                    
                    {Array.from({ length: Math.min(5, transactionsMeta.totalPages) }, (_, i) => {
                      const page = Math.max(1, filters.page - 2) + i;
                      if (page <= transactionsMeta.totalPages) {
                        return (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={page === filters.page ? 'active' : ''}
                          >
                            {page}
                          </button>
                        );
                      }
                      return null;
                    })}
                    
                    <button 
                      onClick={() => handlePageChange(filters.page + 1)}
                      disabled={filters.page >= transactionsMeta.totalPages}
                    >
                      Siguiente »
                    </button>
                    
                    <div className="page-info">
                      Página {filters.page} de {transactionsMeta.totalPages} 
                      ({transactionsMeta.total} tickets total)
                    </div>
                  </Pagination>
                )}
              </>
            )}
          </TransactionList>
        </HistoryContent>
      </HistoryContainer>
    </HistoryModal>
  );
};

export default TicketHistory;